import { getActiveTimezone } from '@/utils/timezone';
import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  customerOrderApi,
  CustomerOrder,
  CustomerOrderStatus,
  CustomerOrderFilters,
} from '@/services/customerOrderApi';
import { branchApi } from '@/services/branchApi';
import { getCurrencySymbol, getActiveCurrency } from '@/utils/currency';
import { StatusUpdateModal } from './StatusUpdateModal';
import { OrderDetailModal } from './OrderDetailModal';
import {
  Search, ShoppingBag, Package, Truck, Bell,
  ArrowUpDown, ChevronLeft, ChevronRight, Download,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import SimpleSelect from '@/components/common/SimpleSelect';

const STATUS_COLORS: Record<CustomerOrderStatus, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  ORDERED: 'bg-yellow-100 text-yellow-700',
  RECEIVED: 'bg-green-100 text-green-700',
  NOTIFIED: 'bg-purple-100 text-purple-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<CustomerOrderStatus, string> = {
  NEW: 'New',
  ORDERED: 'Ordered',
  RECEIVED: 'Received',
  NOTIFIED: 'Notified',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

export default function CustomerOrderList() {
  const [statusModalOrder, setStatusModalOrder] = useState<CustomerOrder | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState<CustomerOrderFilters>({
    page: 1,
    limit: 20,
    search: '',
    status: undefined,
    branchId: undefined,
  });
  const [searchInput, setSearchInput] = useState('');

  // Fetch branches for filter
  const { data: branches } = useQuery({
    queryKey: ['branches-list'],
    queryFn: () => branchApi.getBranchList(),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['admin-customer-order-stats', filters.branchId],
    queryFn: () => customerOrderApi.getStats(filters.branchId),
  });

  // Fetch orders
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['admin-customer-orders', filters],
    queryFn: () => customerOrderApi.getAll(filters),
  });

  const orders = ordersData?.customerOrders || [];
  const pagination = ordersData?.pagination;
  const branchList = branches || [];

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleExport = async (branchId?: string) => {
    try {
      setShowExportMenu(false);
      const blob = await customerOrderApi.exportToExcel(branchId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      const branchName = branchId
        ? branchList.find((b) => b.id === branchId)?.name || 'branch'
        : 'all';
      a.href = url;
      a.download = `customer-orders-${branchName}-${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch {
      toast.error('Failed to export Excel');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: getActiveTimezone() });
  };

  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '-';
    return `${getCurrencySymbol()}${Number(val).toLocaleString(getActiveCurrency().locale)}`;
  };

  const getItemDisplay = (order: CustomerOrder) => {
    return order.brand && order.model
      ? `${order.brand.name} ${order.model.name}`
      : order.itemName || '-';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Customer Orders</h1>
        <div className="relative" ref={exportRef}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4" />
            Export Excel
            <ChevronDown className="w-3 h-3" />
          </button>
          {showExportMenu && (
            <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              <button
                onClick={() => handleExport()}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
              >
                All Branches
              </button>
              {branchList.map((branch) => (
                <button
                  key={branch.id}
                  onClick={() => handleExport(branch.id)}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg"
                >
                  {branch.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600">New</span>
            </div>
            <p className="text-xl font-bold text-blue-700">{stats.new}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-yellow-600" />
              <span className="text-xs font-medium text-yellow-600">Ordered</span>
            </div>
            <p className="text-xl font-bold text-yellow-700">{stats.ordered}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-600">Received</span>
            </div>
            <p className="text-xl font-bold text-green-700">{stats.received}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Bell className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-600">Notified</span>
            </div>
            <p className="text-xl font-bold text-purple-700">{stats.notified}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-600">Delivered</span>
            </div>
            <p className="text-xl font-bold text-emerald-700">{stats.deliveredThisMonth}</p>
            <p className="text-xs text-emerald-500">This month</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by order #, customer, phone, item..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <SimpleSelect
            value={filters.branchId || ''}
            onChange={(v) => setFilters((prev) => ({ ...prev, branchId: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'All Branches' },
              ...branchList.map((b) => ({ value: b.id, label: b.name })),
            ]}
            placeholder="All Branches"
            aria-label="Filter by branch"
            wrapperClassName="w-full sm:w-44"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
          <SimpleSelect
            value={filters.status || ''}
            onChange={(v) => setFilters((prev) => ({ ...prev, status: (v || undefined) as CustomerOrderStatus | undefined, page: 1 }))}
            options={[
              { value: '', label: 'All Statuses' },
              ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
            ]}
            placeholder="All Statuses"
            aria-label="Filter by status"
            wrapperClassName="w-full sm:w-44"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 shrink-0"
          >
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <ShoppingBag className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No customer orders found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b text-left">
                    <th className="px-4 py-3 font-medium text-gray-600">Order #</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Customer</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Item</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-center">Qty</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-right">Price</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-center">Status</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Branch</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Created</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setDetailOrderId(order.id)}
                          className="text-purple-600 hover:text-purple-800 font-medium"
                        >
                          {order.orderNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{order.customer.name}</p>
                        <p className="text-gray-500 text-xs">{order.customer.phone}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-900 max-w-[200px] truncate">
                        {getItemDisplay(order)}
                      </td>
                      <td className="px-4 py-3 text-center">{order.quantity}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(order.expectedPrice)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[order.status]}`}>
                          {STATUS_LABELS[order.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{order.branch?.name || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-500 text-xs">{formatDate(order.createdAt)}</div>
                        <div className="text-gray-400 text-xs">{order.createdBy.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                            <button
                              onClick={() => setStatusModalOrder(order)}
                              title="Update Status"
                              className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            >
                              <ArrowUpDown className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {orders.map((order) => (
                <div key={order.id} className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setDetailOrderId(order.id)}
                      className="text-sm font-semibold text-purple-600"
                    >
                      {order.orderNumber}
                    </button>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[order.status]}`}>
                      {STATUS_LABELS[order.status]}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-900">{order.customer.name}</p>
                      <p className="text-gray-500 text-xs">{order.customer.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-900 text-xs">{getItemDisplay(order)}</p>
                      <p className="text-gray-400 text-xs">{order.branch?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Qty: {order.quantity} | {formatCurrency(order.expectedPrice)}</span>
                    <span>{order.createdBy.name} - {formatDate(order.createdAt)}</span>
                  </div>
                  {order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                    <button
                      onClick={() => setStatusModalOrder(order)}
                      className="px-3 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100"
                    >
                      Update Status
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-gray-500">
              {(pagination.page - 1) * pagination.limit + 1}-
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-1">
              <button
                disabled={pagination.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700 px-2">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {statusModalOrder && (
        <StatusUpdateModal
          isOpen={!!statusModalOrder}
          onClose={() => setStatusModalOrder(null)}
          order={statusModalOrder}
        />
      )}

      {detailOrderId && (
        <OrderDetailModal
          isOpen={!!detailOrderId}
          onClose={() => setDetailOrderId(null)}
          orderId={detailOrderId}
        />
      )}
    </div>
  );
}
